import React, { createContext, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../store';
import { resolveBrand } from './registry';
import type { BrandConfig } from './types';
import { platformBrand } from './platform';

type BrandContextValue = {
  brand: BrandConfig;
};

const BrandContext = createContext<BrandContextValue>({ brand: platformBrand });

type BrandProviderProps = {
  children: React.ReactNode;
  /** Override opcional (páginas públicas já resolvidas). */
  publicBrandKey?: string | null;
  legacyLabel?: string | null;
};

export function BrandProvider({ children, publicBrandKey, legacyLabel }: BrandProviderProps) {
  const parishBrandKey = useSelector(
    (state: RootState) => (state.auth.user?.parish as { brandKey?: string } | undefined)?.brandKey
  );
  const parishName = useSelector((state: RootState) => state.auth.user?.parish?.name);

  const brand = useMemo(
    () =>
      resolveBrand({
        parishBrandKey,
        publicBrandKey,
        legacyLabel: legacyLabel ?? parishName,
      }),
    [parishBrandKey, publicBrandKey, legacyLabel, parishName]
  );

  const value = useMemo(() => ({ brand }), [brand]);

  return <BrandContext.Provider value={value}>{children}</BrandContext.Provider>;
}

export function useBrand(): BrandConfig {
  return useContext(BrandContext).brand;
}

export function useIsBrand(key: BrandConfig['key']): boolean {
  return useBrand().key === key;
}
